import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cpu, HardDrive, Activity, RefreshCw, Gauge } from 'lucide-react';
import { systemAPI } from '../../services/api/adminApi';
import type { CPUStats, GPUStats, DiskStats, NetworkStats, LLMProcess } from '../../types/admin';

interface ResourceError {
  cpu?: string;
  gpu?: string;
  disk?: string;
  network?: string;
  memory?: string;
  processes?: string;
}

const ResourcesPage: React.FC = () => {
  const { t } = useTranslation();
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ResourceError>({});

  const [cpuStats, setCpuStats] = useState<CPUStats | null>(null);
  const [gpuStats, setGpuStats] = useState<GPUStats | null>(null);
  const [diskStats, setDiskStats] = useState<DiskStats | null>(null);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  const [memoryStats, setMemoryStats] = useState<any | null>(null);
  const [llmProcesses, setLlmProcesses] = useState<LLMProcess[]>([]);

  const fetchResources = async () => {
    setLoading(true);
    const newError: ResourceError = {};

    try {
      const resourcesRes = await systemAPI.getResources();
      const data = resourcesRes.data;
      setCpuStats(data.cpu);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to fetch CPU resources:', err);
      newError.cpu = 'Failed to load CPU data';
    }

    try {
      const resourcesRes = await systemAPI.getResources();
      const data = resourcesRes.data;
      setGpuStats(data.gpu);
    } catch (err) {
      console.error('Failed to fetch GPU resources:', err);
      newError.gpu = 'Failed to load GPU data';
    }

    try {
      const resourcesRes = await systemAPI.getResources();
      const data = resourcesRes.data;
      setDiskStats(data.disk);
    } catch (err) {
      console.error('Failed to fetch disk resources:', err);
      newError.disk = 'Failed to load disk data';
    }

    try {
      const resourcesRes = await systemAPI.getResources();
      const data = resourcesRes.data;
      setNetworkStats(data.network);
    } catch (err) {
      console.error('Failed to fetch network resources:', err);
      newError.network = 'Failed to load network data';
    }

    try {
      const resourcesRes = await systemAPI.getResources();
      const data = resourcesRes.data;
      setMemoryStats(data.memory);
    } catch (err) {
      console.error('Failed to fetch memory resources:', err);
      newError.memory = 'Failed to load memory data';
    }

    try {
      const processesRes = await systemAPI.getProcesses();
      setLlmProcesses(processesRes.data);
    } catch (err) {
      console.error('Failed to fetch processes:', err);
      newError.processes = 'Failed to load processes data';
    }

    setError(newError);
    setLoading(false);
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const hasAnyData = cpuStats || gpuStats || diskStats || networkStats || memoryStats;

  if (loading && !hasAnyData) {
    return (
      <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
        <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4'>
          <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>{t('resources.title')}</h1>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-ui-secondary'>Loading...</div>
        </div>
      </div>
    );
  }

  if (!hasAnyData && Object.keys(error).length > 0) {
    return (
      <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
        <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4'>
          <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>{t('resources.title')}</h1>
        </div>
        <div className='flex-1 flex flex-col items-center justify-center gap-4'>
          <div className='text-red-600'>Failed to load resources data</div>
          <button
            onClick={fetchResources}
            className='px-4 py-2 bg-ui-primary text-white rounded-lg hover:bg-ui-primary/90 transition-colors'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
    <div className='bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4'>
      <div className='flex items-center gap-2 text-red-700 text-xs sm:text-sm'>
        <span className='font-medium'>⚠️</span>
        <span>{message}</span>
      </div>
    </div>
  );

  const formatBytes = (gb: number) => {
    return `${gb.toFixed(1)} GB`;
  };

  const getCoresVisualization = (cores: number[]) => {
    return (
      <div className='grid grid-cols-4 gap-1.5 sm:gap-2'>
        {cores.map((core, index) => (
          <div key={index} className='space-y-1'>
            <div className='text-xs text-ui-muted font-body-sm text-center'>C{index + 1}</div>
            <div className='w-full bg-gray-100 rounded-full h-2 sm:h-4'>
              <div
                className='bg-ui-primary h-2 sm:h-4 rounded-full transition-all duration-300'
                style={{ width: `${core}%` }}
              />
            </div>
            <div className='text-xs text-ui-charcoal font-body-sm text-center'>{core}%</div>
          </div>
        ))}
      </div>
    );
  };

  const getTimeSinceLastUpdate = () => {
    if (!lastUpdate) return 'Never';
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);
    return `${diff}s ago`;
  };

  const handleStopProcess = async (pid: number) => {
    if (window.confirm(`Stop process with PID ${pid}?`)) {
      try {
        await systemAPI.stopProcess(pid);
        fetchResources();
      } catch (err) {
        console.error('Failed to stop process:', err);
        alert('Failed to stop process');
      }
    }
  };

  return (
    <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
      {/* Header */}
      <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4 flex-shrink-0'>
        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3'>
          <div>
            <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>
              {t('resources.title')}
            </h1>
            <p className='text-sm text-gray-600 mt-1'>{t('resources.subtitle')}</p>
          </div>
          <div className='flex flex-wrap items-center gap-2 sm:gap-3 sm:mt-6'>
            <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
              {t('resources.lastUpdate')}: {getTimeSinceLastUpdate()}
            </span>
            <button
              onClick={fetchResources}
              disabled={loading}
              className='flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg font-body-sm transition-colors text-xs sm:text-sm bg-ui-primary text-white hover:bg-ui-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className='px-4 sm:p-6 space-y-4 sm:space-y-6 flex-1 overflow-y-auto'>
        {/* CPU and Memory */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
          {/* CPU */}
          {error.cpu && <ErrorBanner message={error.cpu} />}
          {cpuStats && (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center space-x-2'>
                  <Cpu size={18} className='text-ui-primary sm:w-5 sm:h-5' />
                  <h2 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                    {t('resources.cpu')}
                  </h2>
                </div>
              </div>

              <div className='space-y-3 sm:space-y-4'>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                      {t('resources.overall')}
                    </span>
                    <span className='font-display-lg font-bold text-ui-charcoal text-sm sm:text-base'>
                      {cpuStats.overall}%
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 sm:h-4'>
                    <div
                      className='bg-ui-primary h-3 sm:h-4 rounded-full transition-all duration-300'
                      style={{ width: `${cpuStats.overall}%` }}
                    />
                  </div>
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border'>
                  <div className='text-xs sm:text-sm font-body-sm text-ui-muted mb-2 sm:mb-3'>
                    {t('resources.perCoreUsage')}
                  </div>
                  {getCoresVisualization(cpuStats.cores)}
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border grid grid-cols-2 gap-3 sm:gap-4'>
                  <div>
                    <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                      {t('resources.processes')}
                    </div>
                    <div className='font-display-lg font-bold text-ui-charcoal text-sm sm:text-base'>
                      {cpuStats.processes}
                    </div>
                  </div>
                  <div>
                    <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                      {t('resources.threads')}
                    </div>
                    <div className='font-display-lg font-bold text-ui-charcoal text-sm sm:text-base'>
                      {cpuStats.threads}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Memory */}
          {error.memory && <ErrorBanner message={error.memory} />}
          {memoryStats && (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
              <div className='flex items-center space-x-2 mb-4'>
                <Activity size={18} className='text-purple-600 sm:w-5 sm:h-5' />
                <h2 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                  {t('resources.memory')}
                </h2>
              </div>

              <div className='space-y-3 sm:space-y-4'>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                      {t('resources.overall')}
                    </span>
                    <span className='font-display-lg font-bold text-ui-charcoal text-xs sm:text-sm'>
                      {formatBytes(memoryStats.used_gb)}/{formatBytes(memoryStats.total_gb)} GB
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 sm:h-4'>
                    <div
                      className='bg-purple-600 h-3 sm:h-4 rounded-full transition-all duration-300'
                      style={{ width: `${(memoryStats.used_gb / memoryStats.total_gb) * 100}%` }}
                    />
                  </div>
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border space-y-2 sm:space-y-3'>
                  <MemoryBarItem
                    label='System'
                    value={1.9}
                    total={memoryStats.total_gb}
                    color='bg-gray-500'
                  />
                  <MemoryBarItem
                    label='Cached'
                    value={memoryStats.cached_gb}
                    total={memoryStats.total_gb}
                    color='bg-purple-500'
                  />
                  <MemoryBarItem
                    label='Free'
                    value={memoryStats.free_gb}
                    total={memoryStats.total_gb}
                    color='bg-green-500'
                  />
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border'>
                  <div className='flex items-center justify-between'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>Swap</span>
                    <span className='font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                      {formatBytes(memoryStats.swap.used_gb)}/
                      {formatBytes(memoryStats.swap.total_gb)} GB
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-2 mt-2'>
                    <div
                      className='bg-orange-500 h-2 rounded-full transition-all duration-300'
                      style={{
                        width: `${(memoryStats.swap.used_gb / memoryStats.swap.total_gb) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* GPU */}
        {error.gpu && <ErrorBanner message={error.gpu} />}
        {gpuStats && (
          <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
            <div className='flex items-center space-x-2 mb-4'>
              <Gauge size={18} className='text-pink-600 sm:w-5 sm:h-5' />
              <h2 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                {t('resources.gpu')}
              </h2>
              <span className='text-xs sm:text-sm text-ui-muted truncate'>({gpuStats.name})</span>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6'>
              {/* GPU Utilization */}
              <div className='space-y-3 sm:space-y-4'>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>Overall</span>
                    <span className='font-display-lg font-bold text-ui-charcoal text-sm sm:text-base'>
                      {gpuStats.utilization_percent}%
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 sm:h-4'>
                    <div
                      className='bg-pink-600 h-3 sm:h-4 rounded-full transition-all duration-300'
                      style={{ width: `${gpuStats.utilization_percent}%` }}
                    />
                  </div>
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border'>
                  <div className='text-xs sm:text-sm font-body-sm text-ui-muted mb-2 sm:mb-3'>
                    Per-Core Usage
                  </div>
                  <div className='space-y-1.5 sm:space-y-2'>
                    {gpuStats.cores.map((core, index) => (
                      <div key={index} className='space-y-1'>
                        <div className='text-xs text-ui-muted font-body-sm'>C{index + 1}</div>
                        <div className='w-full bg-gray-100 rounded-full h-2 sm:h-3'>
                          <div
                            className='bg-pink-600 h-2 sm:h-3 rounded-full transition-all duration-300'
                            style={{ width: `${core.utilization_percent}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* GPU Memory */}
              <div className='space-y-3 sm:space-y-4'>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>Memory</span>
                    <span className='font-display-lg font-bold text-ui-charcoal text-xs sm:text-sm'>
                      {formatBytes(gpuStats.memory.used_gb)}/{formatBytes(gpuStats.memory.total_gb)}{' '}
                      GB
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 sm:h-4'>
                    <div
                      className='bg-pink-600 h-3 sm:h-4 rounded-full transition-all duration-300'
                      style={{
                        width: `${(gpuStats.memory.used_gb / gpuStats.memory.total_gb) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border'>
                  <div className='text-xs sm:text-sm font-body-sm text-ui-muted mb-2 sm:mb-3'>
                    Processes
                  </div>
                  <div className='space-y-2'>
                    {gpuStats.processes.map((process, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between text-xs sm:text-sm'
                      >
                        <span className='font-body-sm text-ui-charcoal truncate'>
                          {process.name}
                        </span>
                        <span className='font-body-sm text-ui-muted whitespace-nowrap'>
                          {process.utilization_percent}% │ {formatBytes(process.memory_gb)} GB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* GPU Temperature and Power */}
              <div className='space-y-3 sm:space-y-4'>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                      Temperature
                    </span>
                    <span className='font-display-lg font-bold text-ui-charcoal text-sm sm:text-base'>
                      {gpuStats.temperature_celsius}°C
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 sm:h-4'>
                    <div
                      className={`h-3 sm:h-4 rounded-full transition-all duration-300 ${
                        gpuStats.temperature_celsius > 80
                          ? 'bg-red-500'
                          : gpuStats.temperature_celsius > 70
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                      }`}
                      style={{ width: `${(gpuStats.temperature_celsius / 100) * 100}%` }}
                    />
                  </div>
                  <div className='text-xs text-ui-muted mt-1'>Max: 85°C</div>
                </div>

                <div className='pt-3 sm:pt-4 border-t border-ui-border space-y-2 sm:space-y-3'>
                  <div>
                    <div className='flex items-center justify-between'>
                      <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                        Fan Speed
                      </span>
                      <span className='font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {gpuStats.fan_speed_rpm} RPM
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className='flex items-center justify-between'>
                      <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                        Power Usage
                      </span>
                      <span className='font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {gpuStats.power_usage_w}W / {gpuStats.power_limit_w}W
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disk I/O and Network */}
        {(error.disk || error.network) && (
          <>
            {error.disk && <ErrorBanner message={error.disk} />}
            {error.network && <ErrorBanner message={error.network} />}
          </>
        )}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'>
          {/* Disk Read */}
          {diskStats && (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-6'>
              <div className='flex items-center space-x-2 mb-3 sm:mb-4'>
                <HardDrive size={16} className='text-green-600 sm:w-5 sm:h-5' />
                <h2 className='font-heading-md font-semibold text-ui-charcoal text-sm sm:text-base'>
                  Disk Read
                </h2>
              </div>
              <div className='font-display-xl font-bold text-ui-charcoal mb-2 text-lg sm:text-2xl'>
                {diskStats.read_mb_per_sec.toFixed(1)} MB/s
              </div>
              <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                Peak: {diskStats.peak_read_mb_per_sec.toFixed(1)} MB/s
              </div>
            </div>
          )}

          {/* Disk Write */}
          {diskStats && (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-6'>
              <div className='flex items-center space-x-2 mb-3 sm:mb-4'>
                <HardDrive size={16} className='text-blue-600 sm:w-5 sm:h-5' />
                <h2 className='font-heading-md font-semibold text-ui-charcoal text-sm sm:text-base'>
                  Disk Write
                </h2>
              </div>
              <div className='font-display-xl font-bold text-ui-charcoal mb-2 text-lg sm:text-2xl'>
                {diskStats.write_mb_per_sec.toFixed(1)} MB/s
              </div>
              <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                Peak: {diskStats.peak_write_mb_per_sec.toFixed(1)} MB/s
              </div>
            </div>
          )}

          {/* Network */}
          {networkStats && (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-6 sm:col-span-2 lg:col-span-1'>
              <div className='flex items-center space-x-2 mb-3 sm:mb-4'>
                <Activity size={16} className='text-purple-600 sm:w-5 sm:h-5' />
                <h2 className='font-heading-md font-semibold text-ui-charcoal text-sm sm:text-base'>
                  Network
                </h2>
              </div>
              <div className='grid grid-cols-2 gap-3 sm:gap-4'>
                <div>
                  <div className='font-display-lg font-bold text-ui-charcoal text-base sm:text-lg'>
                    {networkStats.download_mb_per_sec.toFixed(1)} MB/s
                  </div>
                  <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>Down</div>
                </div>
                <div>
                  <div className='font-display-lg font-bold text-ui-charcoal text-base sm:text-lg'>
                    {networkStats.upload_mb_per_sec.toFixed(1)} MB/s
                  </div>
                  <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>Up</div>
                </div>
              </div>
              <div className='font-body-sm text-ui-muted mt-2 text-xs sm:text-sm'>
                Peak: {networkStats.peak_mb_per_sec.toFixed(1)} MB/s
              </div>
            </div>
          )}
        </div>

        {/* Active LLM Processes */}
        {error.processes && <ErrorBanner message={error.processes} />}
        {llmProcesses.length > 0 && (
          <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-6'>
            <h2 className='font-heading-lg font-semibold text-ui-charcoal mb-3 sm:mb-4 text-base sm:text-lg'>
              {t('resources.activeLlmProcesses')}
            </h2>

            {/* Mobile Card View */}
            <div className='sm:hidden space-y-3 mb-4'>
              {llmProcesses.map(process => (
                <div
                  key={process.pid}
                  className='border border-ui-border rounded-lg p-3 hover:bg-gray-50 transition-colors'
                >
                  <div className='flex items-center justify-between mb-2'>
                    <div className='flex items-center gap-2'>
                      <div className='w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center'>
                        <span className='text-xs font-semibold text-pink-600'>
                          {process.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className='text-sm font-medium text-ui-charcoal'>{process.name}</div>
                        <div className='text-xs text-ui-muted'>PID: {process.pid}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleStopProcess(process.pid)}
                      className='px-3 py-1.5 text-xs font-body-sm text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors'
                    >
                      Stop
                    </button>
                  </div>
                  <div className='grid grid-cols-2 gap-2 text-xs'>
                    <div className='bg-gray-50 rounded p-2'>
                      <div className='text-ui-muted mb-1'>Model</div>
                      <div className='font-medium text-ui-charcoal truncate'>{process.model}</div>
                    </div>
                    <div className='bg-gray-50 rounded p-2'>
                      <div className='text-ui-muted mb-1'>Duration</div>
                      <div className='font-medium text-ui-charcoal'>{process.duration}</div>
                    </div>
                    <div className='bg-gray-50 rounded p-2'>
                      <div className='text-ui-muted mb-1'>GPU</div>
                      <div className='font-medium text-pink-600'>
                        {process.gpu_utilization_percent !== null &&
                        process.gpu_utilization_percent !== undefined
                          ? `${process.gpu_utilization_percent}%`
                          : 'N/A'}
                      </div>
                    </div>
                    <div className='bg-gray-50 rounded p-2'>
                      <div className='text-ui-muted mb-1'>Memory</div>
                      <div className='font-medium text-ui-charcoal'>
                        {formatBytes(process.memory_gb)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className='hidden sm:block overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-ui-border'>
                  <tr>
                    <th className='px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      Process
                    </th>
                    <th className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      PID
                    </th>
                    <th className='hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      Model
                    </th>
                    <th className='px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      GPU
                    </th>
                    <th className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      CPU
                    </th>
                    <th className='hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      Memory
                    </th>
                    <th className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      Duration
                    </th>
                    <th className='px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-ui-border'>
                  {llmProcesses.map(process => (
                    <tr key={process.pid} className='hover:bg-gray-50'>
                      <td className='px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.name}
                      </td>
                      <td className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.pid}
                      </td>
                      <td className='hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.model}
                      </td>
                      <td className='px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.gpu_utilization_percent !== null &&
                        process.gpu_utilization_percent !== undefined
                          ? `${process.gpu_utilization_percent}%`
                          : 'N/A'}
                      </td>
                      <td className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.cpu_utilization_percent}%
                      </td>
                      <td className='hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {formatBytes(process.memory_gb)}
                      </td>
                      <td className='hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 font-body-sm text-ui-charcoal text-xs sm:text-sm'>
                        {process.duration}
                      </td>
                      <td className='px-2 sm:px-4 py-2 sm:py-3'>
                        <button
                          onClick={() => handleStopProcess(process.pid)}
                          className='px-2 sm:px-3 py-1 text-xs font-body-sm text-red-600 hover:bg-red-50 rounded transition-colors'
                        >
                          Stop
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface MemoryBarItemProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

const MemoryBarItem: React.FC<MemoryBarItemProps> = ({ label, value, total, color }) => {
  const percentage = (value / total) * 100;
  return (
    <div>
      <div className='flex items-center justify-between text-xs sm:text-sm'>
        <span className='font-body-sm text-ui-charcoal'>{label}</span>
        <span className='font-body-sm text-ui-muted'>{formatBytes(value)} GB</span>
      </div>
      <div className='w-full bg-gray-100 rounded-full h-1.5 sm:h-2 mt-1'>
        <div
          className={`${color} h-1.5 sm:h-2 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const formatBytes = (gb: number) => {
  return `${gb.toFixed(1)}`;
};

export default ResourcesPage;
